import { Link } from "react-router-dom";
import { LogoFull } from "./Logo";
import { useEffect } from "react";

export default function Privacy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-50">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="logo-link"><LogoFull size={30} /></Link>
          <nav className="flex items-center gap-3">
            <Link to="/login" className="px-4 py-2 font-semibold text-[#0A0A0A] hover:text-[#1A3DFF] transition-colors text-[15px]">Sign in</Link>
            <Link to="/register" className="btn-primary">Get started free</Link>
          </nav>
        </div>
      </header>

      <div className="max-w-[720px] mx-auto px-6 py-16">
        <h1 className="font-display text-[36px] tracking-[-0.03em] text-[#0A0A0A] mb-2" data-testid="privacy-title">Privacy Policy</h1>
        <p className="text-[13px] text-[#9CA3AF] mb-12">Last updated: April 7, 2026</p>

        <div className="prose-verobill space-y-8 text-[15px] leading-[1.75] text-[#374151]">
          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">1. Who we are</h2>
            <p>VeroBill ("we", "us", "our") is a product of Brightridge Partners Ltd, a company registered in England and Wales. We provide an online invoicing platform at verobill.com (the "Service"). This Privacy Policy explains how we collect, use, and protect your personal data when you use our Service.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">2. Information we collect</h2>
            <p><strong>Account information:</strong> When you register, we collect your name, email address, and a hashed password. We may also collect your business name, address, and logo if you choose to provide them.</p>
            <p><strong>Invoice data:</strong> We store the invoices, estimates, expenses, and client information you create through the Service. This data belongs to you.</p>
            <p><strong>Payment credentials:</strong> If you connect payment providers (Stripe, PayPal, Razorpay, Wise), we store your API keys in encrypted form using industry-standard Fernet symmetric encryption. We never store your customers' payment card details — those are handled directly by the payment provider.</p>
            <p><strong>Usage data:</strong> We collect basic analytics (page views, feature usage) to improve the Service. We use PostHog for product analytics.</p>
            <p><strong>Cookies:</strong> We use essential cookies for authentication (JWT session tokens). We do not use advertising or tracking cookies.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">3. How we use your data</h2>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>To provide and maintain the Service</li>
              <li>To process your invoices, estimates, and expenses</li>
              <li>To send invoice emails on your behalf (via Resend)</li>
              <li>To process payments through your connected payment providers</li>
              <li>To communicate with you about your account, billing, and service updates</li>
              <li>To improve the Service based on aggregated usage patterns</li>
            </ul>
            <p className="mt-3">We do <strong>not</strong> sell, rent, or share your personal data with third parties for marketing purposes. Ever.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">4. Third-party services</h2>
            <p>We use the following third-party services to operate:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>MongoDB Atlas</strong> — Database hosting (your data is stored securely)</li>
              <li><strong>Stripe</strong> — Subscription billing for VeroBill plans</li>
              <li><strong>Resend</strong> — Transactional email delivery (sending invoices to your clients)</li>
              <li><strong>PostHog</strong> — Product analytics (anonymised usage data)</li>
              <li><strong>OpenAI</strong> — AI blog content generation (admin feature only, no customer data is sent)</li>
            </ul>
            <p className="mt-3">Each of these providers has their own privacy policy and data processing agreements. Your payment provider credentials are stored using industry-standard encryption and are only accessed when needed to process payments on your behalf.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">5. Data security</h2>
            <p>We take security seriously:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Passwords are hashed with bcrypt (never stored in plain text)</li>
              <li>Payment API keys are encrypted with Fernet symmetric encryption</li>
              <li>All data is transmitted over HTTPS/TLS</li>
              <li>Authentication uses secure HTTP-only JWT cookies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">6. Data retention</h2>
            <p>We retain your data for as long as your account is active. If you delete your account, we will delete your personal data within 30 days. Aggregated, anonymised analytics data may be retained indefinitely.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">7. Your rights (GDPR & CCPA)</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>Access</strong> your personal data</li>
              <li><strong>Correct</strong> inaccurate data</li>
              <li><strong>Delete</strong> your account and all associated data</li>
              <li><strong>Export</strong> your invoice and client data</li>
              <li><strong>Object</strong> to processing of your data</li>
            </ul>
            <p className="mt-3">To exercise any of these rights, contact us at <strong>hello@verobill.com</strong>.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">8. Children's privacy</h2>
            <p>The Service is not intended for use by anyone under the age of 16. We do not knowingly collect data from children.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">9. Changes to this policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of material changes by email or through a notice on the Service. Your continued use of the Service after changes constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">10. Contact</h2>
            <p>If you have questions about this Privacy Policy or your data, contact us at:</p>
            <p className="mt-2"><strong>Email:</strong> hello@verobill.com</p>
            <p className="mt-1"><strong>Company:</strong> Brightridge Partners Ltd</p>
            <p className="mt-1">71-75 Shelton Street, Covent Garden, London, WC2H 9JQ, United Kingdom</p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[#E5E7EB] py-8 px-6 bg-white">
        <div className="max-w-[720px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <LogoFull size={22} />
          <div className="flex items-center gap-4 text-[12px] text-[#9CA3AF]">
            <Link to="/terms" className="hover:text-[#0A0A0A]">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-[#0A0A0A]">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
